import { useClient } from "@/composables/useClient";
import { deserializeCbiDto, serializeCbiDto, encodeRequestParam } from "../serialization/CbiDateCodec";
import type { ClassLocationApi } from "../../api/ClassLocationApi";
import type { ApClassFetchOptions } from "../../generated/dto/ApClassFetchOptions";
import type { ApClassStaff } from "../../generated/dto/ApClassStaff";
import type { ClassLocationFetchOptions } from "../../generated/dto/ClassLocationFetchOptions";
import type { ClassLocationStaff } from "../../generated/dto/ClassLocationStaff";
import type { CreateClassLocation } from "../../generated/dto/CreateClassLocation";
import type { DeleteClassLocationStaff } from "../../generated/dto/DeleteClassLocationStaff";
import type { JpClassFetchOptions } from "../../generated/dto/JpClassFetchOptions";
import type { JpClassStaff } from "../../generated/dto/JpClassStaff";
import type { UpdateClassLocation } from "../../generated/dto/UpdateClassLocation";

export class ClassLocationApiClient implements ClassLocationApi {
  constructor(private readonly client = useClient()) {}

  async getClassLocationStaff(id: number, options?: ClassLocationFetchOptions): Promise<ClassLocationStaff> {
    const { data } = await this.client.get<unknown>(`/classlocation/staff/${id}`, { params: serializeCbiDto(options, "generated.dto.ClassLocationFetchOptions") });
    return deserializeCbiDto<ClassLocationStaff>(data, "generated.dto.ClassLocationStaff");
  }

  async createClassLocationStaff(source: CreateClassLocation): Promise<ClassLocationStaff> {
    const { data } = await this.client.post<unknown>(`/classlocation/staff`, serializeCbiDto(source, "generated.dto.CreateClassLocation"));
    return deserializeCbiDto<ClassLocationStaff>(data, "generated.dto.ClassLocationStaff");
  }

  async updateClassLocationStaff(source: UpdateClassLocation): Promise<ClassLocationStaff> {
    const { data } = await this.client.put<unknown>(`/classlocation/staff/${source.locationId}`, serializeCbiDto(source, "generated.dto.UpdateClassLocation"));
    return deserializeCbiDto<ClassLocationStaff>(data, "generated.dto.ClassLocationStaff");
  }

  async deleteClassLocationStaff(source: DeleteClassLocationStaff): Promise<void> {
    await this.client.delete(`/classlocation/staff/${source.locationId}`);
  }

  async getClassLocationStaffApClasses(id: number, options?: ApClassFetchOptions): Promise<Array<ApClassStaff>> {
    const { data } = await this.client.get<unknown>(`/classlocation/staff/${id}/apclasses`, { params: serializeCbiDto(options, "generated.dto.ApClassFetchOptions") });
    return deserializeCbiDto<Array<ApClassStaff>>(data, "generated.dto.ApClassStaff");
  }

  async getClassLocationStaffJpClasses(id: number, options?: JpClassFetchOptions): Promise<Array<JpClassStaff>> {
    const { data } = await this.client.get<unknown>(`/classlocation/staff/${id}/jpclasses`, { params: serializeCbiDto(options, "generated.dto.JpClassFetchOptions") });
    return deserializeCbiDto<Array<JpClassStaff>>(data, "generated.dto.JpClassStaff");
  }
}

export const useClassLocationApi = (): ClassLocationApi => new ClassLocationApiClient();
